/**
 * Service for managing email tracking preferences (open & click tracking)
 * Stores user's last preference in localStorage
 */

import { Person } from '../types/person'
import { TeamPrivacySettings } from '../types/user'
import { TeamEmailSettings } from '../types/teamEmailSettings'
import { FindByEmailResponse } from '../types/loft'

type TrackingPreferences = {
  trackOpens: boolean
  trackClicks: boolean
}

type RecipientWithEUFlag = Person | FindByEmailResponse | { eu_resident?: boolean }

type ResolvePreferencesParams = {
  teamDefaults: { openTracking: boolean; clickTracking: boolean }
  overrideDisabled?: { clickTracking: boolean; openTracking: boolean }
  personalOverride?: { clickTracking?: boolean; openTracking?: boolean }
  recipient?: RecipientWithEUFlag | null
  teamPrivacySettings?: TeamPrivacySettings | null
}

const TRACKING_PREFERENCES_KEY = 'emailTrackingPreferences'

/**
 * Calculate if tracking is disabled due to EU resident override
 */
export const calculateEUOverrideDisabled = (
  recipient: RecipientWithEUFlag | null | undefined,
  teamPrivacySettings: TeamPrivacySettings | null | undefined
): { clickTracking: boolean; openTracking: boolean } => {
  const isEUResident = !!recipient?.eu_resident

  return {
    clickTracking:
      typeof teamPrivacySettings?.click_tracking_eu_resident === 'boolean'
        ? isEUResident && !teamPrivacySettings.click_tracking_eu_resident
        : false,
    openTracking:
      typeof teamPrivacySettings?.open_tracking_eu_resident === 'boolean'
        ? isEUResident && !teamPrivacySettings.open_tracking_eu_resident
        : false,
  }
}

/**
 * Calculate if tracking override is disabled for non-admins
 */
export const calculateOverrideDisabled = (
  teamEmailSettings: TeamEmailSettings | null | undefined,
  isTeamAdmin: boolean
): { clickTracking: boolean; openTracking: boolean } => {
  return {
    clickTracking: !teamEmailSettings?.allow_click_tracking_override && !isTeamAdmin,
    openTracking: !teamEmailSettings?.allow_open_tracking_override && !isTeamAdmin,
  }
}

/**
 * Get tracking preferences from localStorage
 * Preferences are stored per-browser, not per-user
 */
export const getStoredTrackingPreferences = (): TrackingPreferences | null => {
  try {
    const stored = localStorage.getItem(TRACKING_PREFERENCES_KEY)
    if (!stored) return null

    return JSON.parse(stored)
  } catch (error) {
    console.warn('Failed to get tracking preferences:', error)
    return null
  }
}

/**
 * Save tracking preferences to localStorage
 */
export const saveTrackingPreferences = (preferences: TrackingPreferences): void => {
  try {
    localStorage.setItem(TRACKING_PREFERENCES_KEY, JSON.stringify(preferences))
  } catch (error) {
    console.warn('Failed to save tracking preferences:', error)
  }
}

/**
 * Determine tracking preferences based on full hierarchy
 *
 * Priority Order:
 * 1. EU Override - If recipient is EU resident and team disables EU tracking, force OFF
 * 2. Team Override Disabled - If team admin disables override, use team default
 * 3. Last Saved - User's last saved preference from localStorage (per-browser, not per-user)
 * 4. Personal Override - User's personal override setting
 * 5. Default - true
 */
export const resolveTrackingPreferences = (
  teamDefaults: { openTracking: boolean; clickTracking: boolean },
  options?: Omit<ResolvePreferencesParams, 'teamDefaults'>
): TrackingPreferences => {
  const { overrideDisabled, personalOverride, recipient, teamPrivacySettings } = options || {}
  const euOverrideDisabled = calculateEUOverrideDisabled(recipient, teamPrivacySettings)

  let trackOpens: boolean
  if (euOverrideDisabled.openTracking) {
    trackOpens = false
  } else if (overrideDisabled?.openTracking) {
    trackOpens = teamDefaults.openTracking
  } else {
    trackOpens = resolveOpenTracking(personalOverride?.openTracking)
  }

  let trackClicks: boolean
  if (euOverrideDisabled.clickTracking) {
    trackClicks = false
  } else if (overrideDisabled?.clickTracking) {
    trackClicks = teamDefaults.clickTracking
  } else {
    trackClicks = resolveClickTracking(personalOverride?.clickTracking)
  }

  return { trackOpens, trackClicks }
}

/**
 * Resolve open tracking preference
 * Priority: Last Saved → Personal Override → Default (true)
 */
function resolveOpenTracking(personalOverride?: boolean): boolean {
  const stored = getStoredTrackingPreferences()

  if (stored !== null && stored.trackOpens !== undefined) {
    return stored.trackOpens
  }

  if (personalOverride !== undefined) {
    return personalOverride
  }

  return true
}

/**
 * Resolve click tracking preference
 * Priority: Last Saved → Personal Override → Default (true)
 */
function resolveClickTracking(personalOverride?: boolean): boolean {
  const stored = getStoredTrackingPreferences()

  if (stored !== null && stored.trackClicks !== undefined) {
    return stored.trackClicks
  }

  if (personalOverride !== undefined) {
    return personalOverride
  }

  return true
}
